import type { components, operations } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que leen y escriben las pantallas de la orden (#131), sobre el backend de
// #130. La orden de muestra de la fase 3: de acá se copia cómo hablan con la API
// los bloques del tramo 3 —líneas, estados, montos, fotos, gastos—.

export type CabeceraOrden = components['schemas']['ServiceOrderHeader'];
export type Asignable = components['schemas']['AssignableStaffItem'];
export type EstadoOrden = components['schemas']['OrderStatusItem'];
export type LineaOrden = components['schemas']['ServiceOrderLineItem'];
export type ItemDeCatalogo = components['schemas']['OrderableItem'];
export type MontosOrden = components['schemas']['ServiceOrderAmounts'];
export type EntradaHistorial = components['schemas']['ServiceOrderHistoryEntry'];

/// El taller de la pestaña (#205), en toda clave de caché: regla 5 de multitenancy.
function useTaller(): string {
  return useTallerDeLaCache();
}

const claves = {
  todo: (taller: string) => ['ordenes', taller] as const,
  listado: (taller: string, sucursal: string, filtros: FiltrosListado) =>
    ['ordenes', taller, 'listado', sucursal, filtros] as const,
  ficha: (taller: string, id: string) => ['ordenes', taller, 'ficha', id] as const,
  asignables: (taller: string, sucursal: string) => ['ordenes', taller, 'asignables', sucursal] as const,
  estados: (taller: string) => ['ordenes', taller, 'estados'] as const,
  lineas: (taller: string, id: string) => ['ordenes', taller, 'lineas', id] as const,
  montos: (taller: string, id: string) => ['ordenes', taller, 'montos', id] as const,
  // Bajo `ordenes`: todo cambio a la orden invalida esa rama, y el historial se relee solo.
  historial: (taller: string, id: string) => ['ordenes', taller, 'historial', id] as const,
};

export type OrdenEnListado = components['schemas']['ServiceOrderListItem'];
type ConsultaListado = NonNullable<operations['ListServiceOrders']['parameters']['query']>;

/** Desde cuándo se abrió: días contados hacia atrás desde hoy, en la hora de quien mira. */
export type PeriodoListado = 'cualquiera' | 'hoy' | 'semana' | 'mes';

export interface FiltrosListado {
  buscar: string;
  /** `activas` —las que siguen en el taller: ni entregadas ni anuladas—, `todos`, o el id de un estado. */
  estado: string;
  /** `todos`, o el id de una persona. */
  asignado: string;
  periodo: PeriodoListado;
  cursor: string | null;
}

const DIAS: Record<Exclude<PeriodoListado, 'cualquiera'>, number> = { hoy: 1, semana: 7, mes: 30 };

/**
 * La medianoche de hace `dias - 1` días **en la hora de quien mira**, en UTC: «hoy»
 * en Guatemala empieza a las 06:00 UTC. La API recibe el instante y no adivina
 * zonas horarias (#138).
 */
function desde(periodo: PeriodoListado): string | undefined {
  if (periodo === 'cualquiera') return undefined;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - (DIAS[periodo] - 1));
  return d.toISOString();
}

/**
 * Lo que se le pregunta a la API con los filtros de la pantalla y la sucursal
 * del app bar.
 */
function consultaDe(filtros: FiltrosListado, sucursal: string): ConsultaListado {
  const buscar = filtros.buscar.trim();
  const abiertaDesde = desde(filtros.periodo);

  // Lo escrito en el buscador pasa por encima de la sucursal y del estado: una
  // placa trae **todas** las órdenes de ese carro —el historial por activo—,
  // también las entregadas y las de otra sucursal. Asignado y fecha sí se
  // respetan: esos los puso la persona a propósito.
  const buscando = buscar !== '';
  const estado = buscando ? 'todos' : filtros.estado;

  return {
    ...(!buscando && { locationId: sucursal }),
    ...(estado === 'activas' && { inShop: true }),
    ...(estado !== 'activas' && estado !== 'todos' && { statusId: estado }),
    ...(filtros.asignado !== 'todos' && { assignedTo: filtros.asignado }),
    ...(abiertaDesde && { openedFrom: abiertaDesde }),
    ...(buscar && { search: buscar }),
    ...(filtros.cursor && { cursor: filtros.cursor }),
  };
}

/**
 * El listado (#139), sobre `GET /api/service-orders` (#138). **Sin montos**: lo ve
 * también quien no tiene `order.amount.view`, y la columna Total del Figma no
 * está (ver `docs/figma.md`, «Lo decidido»).
 */
export function useListadoOrdenes(sucursal: string, filtros: FiltrosListado) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.listado(taller, sucursal, filtros),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/service-orders', {
        params: { query: consultaDe(filtros, sucursal) },
      });
      if (error || !data) throw new Error('No se pudo leer el listado');
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/** Los estados activos del taller, en su orden: el combo «Cambiar estado». */
export function useEstados() {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.estados(taller),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/order-statuses');
      if (error || !data) throw new Error('No se pudieron leer los estados');
      return data;
    },
  });
}

/** La versión llega como `number | string` —un `uint` en el contrato—. Se manda como número. */
export const version = (v: number | string) => Number(v);

/**
 * La clave de reintento de un formulario (B-32): **la misma mientras se mande lo
 * mismo**. Un doble clic o una respuesta que se perdió reenvían el mismo cuerpo
 * con la misma clave, y la API abre una sola orden. Si la persona corrige algo y
 * vuelve a enviar, es otro pedido y lleva otra clave: con la de antes, la API
 * respondería `idempotency.clave_reusada` en cada intento y no habría salida.
 *
 * Es lo que va a hacer la PWA con su cola sin señal: la clave nace con la
 * intención, no con cada envío.
 */
export function useClaveDeReintento(): (cuerpo: unknown) => string {
  const ultima = useRef<{ clave: string; cuerpo: string } | null>(null);

  return (cuerpo) => {
    const texto = JSON.stringify(cuerpo);
    if (ultima.current?.cuerpo !== texto) ultima.current = { clave: crypto.randomUUID(), cuerpo: texto };
    return ultima.current.clave;
  };
}

/** La cabecera de la orden, o `null` si no existe en este taller. */
export function useCabeceraOrden(id: string | undefined) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.ficha(taller, id ?? ''),
    enabled: id !== undefined,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/service-orders/{id}', {
        params: { path: { id: id ?? '' } },
      });
      if (response.status === 404) return null;
      if (error || !data) throw new Error('No se pudo leer la orden');
      return data;
    },
  });
}

export type ActivoEncontrado = components['schemas']['AssetListItem'];

/** Cuántos carros se ofrecen al escribir la placa: con más, la placa está a medias. */
const MAX_ENCONTRADOS = 5;

/**
 * Los activos cuya placa —o código— se parece a lo escrito. La API compara sin
 * guiones ni espacios: `p 123-abc` encuentra `P123ABC`. Con menos de dos letras no
 * se pregunta: saldrían todos.
 *
 * **Con los desactivados**: la placa de un carro desactivado sigue ocupada, así
 * que registrarlo de nuevo chocaría. Cuando vuelve al taller, se elige el que ya
 * existe, y la lista lo marca.
 */
export function useBuscarPorPlaca(texto: string) {
  const taller = useTaller();
  const limpio = texto.trim();

  return useQuery({
    queryKey: ['activos', taller, 'por-placa', limpio] as const,
    enabled: limpio.length >= 2,
    queryFn: () => buscarPorPlaca(limpio),
  });
}

/**
 * La misma búsqueda, leída una vez. La usa «Usar ese activo →» (#147) para elegir
 * el carro existente con lo que solo trae la búsqueda: si su dueño está inactivo.
 */
export async function buscarPorPlaca(texto: string, limite = MAX_ENCONTRADOS): Promise<ActivoEncontrado[]> {
  const { data, error } = await api.GET('/api/assets', {
    params: { query: { search: texto.trim(), limit: limite } },
  });
  if (error || !data) throw new Error('No se pudo buscar el activo');
  return data.items;
}

/**
 * La ficha de un activo, leída una vez: la del carro que se acaba de registrar en
 * la ventana, para elegirlo. No es una consulta que se mantenga al día: se lee
 * cuando se crea y ya.
 */
export async function leerActivo(id: string): Promise<components['schemas']['AssetDetail'] | null> {
  const { data } = await api.GET('/api/assets/{id}', { params: { path: { id } } });
  return data ?? null;
}

/** A quién se le puede asignar una orden de esa sucursal. Solo id y nombre. */
export function useAsignables(sucursal: string | null) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.asignables(taller, sucursal ?? ''),
    enabled: sucursal !== null,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/service-orders/assignable-staff', {
        params: { query: { locationId: sucursal ?? '' } },
      });
      if (error || !data) throw new Error('No se pudo leer el personal');
      return data;
    },
  });
}

/** Lo que manda la apertura. Lo vacío no viaja: un id opcional no admite `null` en el contrato. */
export interface EnvioApertura {
  locationId: string;
  customerId: string;
  assetId: string;
  reportedIssue: string;
  assignedTo: string;
}

/** Lo que manda la edición: **la cabecera entera** como debe quedar. */
export interface EnvioCabecera {
  reportedIssue: string;
  notes: string;
  /** `datetime-local`, en la hora de quien escribe: se manda en UTC. */
  promisedAt: string;
  externalReference: string;
  customerId: string;
  assetId: string;
  assignedTo: string;
}

const siHay = <K extends string>(clave: K, valor: string) =>
  valor.trim() ? ({ [clave]: valor.trim() } as Record<K, string>) : {};

/**
 * Las escrituras, con la forma que espera `useFormulario`: el `ProblemDetails` o
 * nada. Cada una lleva su `Idempotency-Key` y **siempre** invalida lo de órdenes:
 * con o sin error, la versión que hay que mandar la próxima vez puede haber
 * cambiado.
 */
export function useCambiosOrden() {
  const taller = useTaller();
  const cache = useQueryClient();

  return {
    /** `alAbrir` recibe el id de la orden nueva. */
    abrir: async (clave: string, v: EnvioApertura, alAbrir: (id: string) => void) => {
      const { data, error } = await api.POST('/api/service-orders', {
        headers: { 'Idempotency-Key': clave },
        body: {
          locationId: v.locationId,
          ...siHay('customerId', v.customerId),
          ...siHay('assetId', v.assetId),
          ...siHay('reportedIssue', v.reportedIssue),
          ...siHay('assignedTo', v.assignedTo),
        },
      });
      if (data) alAbrir(String(data.id));
      await cache.invalidateQueries({ queryKey: claves.todo(taller) });
      return error ? (error as ProblemaApi) : undefined;
    },

    editar: async (clave: string, id: string, versionActual: number, v: EnvioCabecera) => {
      const { error } = await api.PUT('/api/service-orders/{id}', {
        headers: { 'Idempotency-Key': clave },
        params: { path: { id } },
        body: {
          version: versionActual,
          ...siHay('reportedIssue', v.reportedIssue),
          ...siHay('notes', v.notes),
          ...(v.promisedAt.trim() && { promisedAt: new Date(v.promisedAt).toISOString() }),
          ...siHay('externalReference', v.externalReference),
          ...siHay('customerId', v.customerId),
          ...siHay('assetId', v.assetId),
          ...siHay('assignedTo', v.assignedTo),
        },
      });
      await cache.invalidateQueries({ queryKey: claves.todo(taller) });
      return error ? (error as ProblemaApi) : undefined;
    },

    /** El motivo, solo al anular. */
    cambiarEstado: async (clave: string, id: string, versionActual: number, estado: string, motivo?: string) => {
      const { error } = await api.PUT('/api/service-orders/{id}/status', {
        headers: { 'Idempotency-Key': clave },
        params: { path: { id } },
        body: { version: versionActual, statusId: estado, ...(motivo?.trim() && { cancellationReason: motivo.trim() }) },
      });
      await cache.invalidateQueries({ queryKey: claves.todo(taller) });
      return error ? (error as ProblemaApi) : undefined;
    },

    /**
     * Si sale bien, lo de órdenes **se marca viejo sin volver a pedirse**: la ficha
     * que está abierta pediría una orden que ya no existe y alcanzaría a decir «no
     * existe» antes de que la pantalla se vaya. Lo que se monte después, sí lo pide.
     */
    eliminar: async (id: string) => {
      const { error } = await api.DELETE('/api/service-orders/{id}', { params: { path: { id } } });
      if (error) {
        await cache.invalidateQueries({ queryKey: claves.todo(taller) });
        return error as ProblemaApi;
      }
      void cache.invalidateQueries({ queryKey: claves.todo(taller), refetchType: 'none' });
      return undefined;
    },
  };
}

/** Las líneas vivas de la orden, en el orden en que se agregaron. **Sin montos**: van por lo suyo (#137). */
export function useLineasOrden(id: string) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.lineas(taller, id),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/service-orders/{id}/lines', { params: { path: { id } } });
      if (error || !data) throw new Error('No se pudieron leer las líneas');
      return data;
    },
  });
}

/**
 * Los servicios y productos **activos** que se parecen a lo escrito, para agregar
 * una línea o ligar una anotación. Con menos de dos letras no se pregunta.
 *
 * Por su propio endpoint y no por el listado del catálogo: ese pide
 * `catalog.edit` y trae el precio de lista, y agregar una línea lo hace también
 * quien solo tiene `order.edit`. La clave va fuera de `ordenes`: cada escritura
 * invalida esa rama, y volver a buscar no le sirve a nadie.
 */
export function useBuscarEnCatalogo(texto: string) {
  const taller = useTaller();
  const limpio = texto.trim();

  return useQuery({
    queryKey: ['itemsParaOrden', taller, limpio] as const,
    enabled: limpio.length >= 2,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/service-orders/orderable-items', {
        params: { query: { search: limpio } },
      });
      if (error || !data) throw new Error('No se pudo buscar en el catálogo');
      return data;
    },
  });
}

/**
 * Lo que manda «Agregar línea». Con `catalogItemId` es del catálogo; sin él y
 * con `kind`, una línea libre (#162); sin ninguno de los dos, una anotación.
 */
export interface EnvioLinea {
  /** El id de la línea lo pone quien la crea (#132). */
  id: string;
  catalogItemId: string;
  rawText: string;
  /** Solo la línea libre: con ítem, la clase la da el catálogo. */
  kind: 'service' | 'product' | null;
  /** Una anotación no tiene cantidad. */
  quantity: number | null;
}

/**
 * Las escrituras de las líneas (#133), sobre los endpoints de #132. Como las de
 * la orden: devuelven el `ProblemDetails` o nada, llevan su `Idempotency-Key` e
 * invalidan **todo** lo de órdenes —las que mueven montos cambian también la
 * versión de la cabecera, y el próximo cambio de estado tiene que mandar la nueva—.
 *
 * Las líneas no llevan versión: dos personas pueden marcar líneas distintas de
 * la misma orden a la vez.
 */
export function useCambiosLinea(orden: string) {
  const taller = useTaller();
  const cache = useQueryClient();

  async function terminar(error: unknown) {
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return error ? (error as ProblemaApi) : undefined;
  }

  const ruta = (linea: string) => ({ path: { id: orden, lineId: linea } });

  return {
    agregar: async (clave: string, v: EnvioLinea) => {
      const { error } = await api.POST('/api/service-orders/{id}/lines', {
        headers: { 'Idempotency-Key': clave },
        params: { path: { id: orden } },
        body: {
          id: v.id,
          ...siHay('catalogItemId', v.catalogItemId),
          ...siHay('rawText', v.rawText),
          ...(!v.catalogItemId && v.kind && { kind: v.kind }),
          ...((v.catalogItemId || v.kind) && v.quantity !== null && { quantity: v.quantity }),
        },
      });
      return terminar(error);
    },

    ligar: async (clave: string, linea: string, item: string) => {
      const { error } = await api.PUT('/api/service-orders/{id}/lines/{lineId}/catalog-item', {
        headers: { 'Idempotency-Key': clave },
        params: ruta(linea),
        body: { catalogItemId: item },
      });
      return terminar(error);
    },

    cambiarCantidad: async (clave: string, linea: string, cantidad: number) => {
      const { error } = await api.PUT('/api/service-orders/{id}/lines/{lineId}/quantity', {
        headers: { 'Idempotency-Key': clave },
        params: ruta(linea),
        body: { quantity: cantidad },
      });
      return terminar(error);
    },

    /** `persona` vacío la devuelve a quien tenga la orden. */
    asignar: async (clave: string, linea: string, persona: string) => {
      const { error } = await api.PUT('/api/service-orders/{id}/lines/{lineId}/assignee', {
        headers: { 'Idempotency-Key': clave },
        params: ruta(linea),
        body: { ...siHay('assignedTo', persona) },
      });
      return terminar(error);
    },

    // Cumplir, reabrir y quitar llevan **una clave nueva por toque**: no tienen
    // cuerpo que distinga un pedido del anterior, y con la misma clave un
    // cumplir → reabrir → cumplir respondería lo guardado sin cumplir nada.

    /** A nombre de quien entra, y ahora. */
    cumplir: async (linea: string) => {
      const { error } = await api.POST('/api/service-orders/{id}/lines/{lineId}/completion', {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        params: ruta(linea),
      });
      return terminar(error);
    },

    reabrir: async (linea: string) => {
      const { error } = await api.DELETE('/api/service-orders/{id}/lines/{lineId}/completion', {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        params: ruta(linea),
      });
      return terminar(error);
    },

    quitar: async (linea: string) => {
      const { error } = await api.DELETE('/api/service-orders/{id}/lines/{lineId}', {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        params: ruta(linea),
      });
      return terminar(error);
    },
  };
}

/** Un monto como llega del contrato —`number | string`, un `decimal`—, o `null` si no hay. */
export const montoDe = (v: number | string | null) => (v === null ? null : Number(v));

/**
 * Lo que se cobra por la orden (#137), por su propio endpoint (#136). **Solo se
 * pide con `order.amount.view`**: sin él la API responde 403, y la sección no
 * dibuja montos. Esconderlos es comodidad; lo que protege el dato es el servidor.
 */
/**
 * El historial de la orden (#141): la apertura, los estados, la cabecera, el total
 * fijado a mano, las líneas agregadas y quitadas y las fotos, lo más nuevo primero. Pide `order.view`; los montos del
 * total llegan solo con `order.amount.view`, y si no, vienen nulos desde el servidor.
 *
 * Solo se pide con el panel abierto: no al abrir la ficha (#193).
 */
export function useHistorialOrden(id: string, abierto: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.historial(taller, id),
    enabled: abierto,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/service-orders/{id}/history', { params: { path: { id } } });
      if (error || !data) throw new Error('No se pudo leer el historial de la orden');
      return data;
    },
  });
}

async function pedirMontos(id: string): Promise<MontosOrden> {
  const { data, error } = await api.GET('/api/service-orders/{id}/amounts', { params: { path: { id } } });
  if (error || !data) throw new Error('No se pudieron leer los montos');
  return data;
}

export function useMontosOrden(id: string, habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.montos(taller, id),
    enabled: habilitado,
    queryFn: () => pedirMontos(id),
  });
}

/**
 * Las escrituras de los montos (#137): el precio de una línea y el total a mano.
 * Piden `order.amount.edit`. Como las demás, invalidan todo lo de órdenes: un
 * precio mueve el total y la versión de la cabecera.
 *
 * **`null` se manda siempre a propósito**: en el precio deja la línea sin precio,
 * y en el total lo devuelve a seguir la suma. El contrato exige el campo, y un
 * cuerpo sin él se leería igual que `null` (#159).
 */
export function useCambiosMontos(orden: string) {
  const taller = useTaller();
  const cache = useQueryClient();

  async function terminar(error: unknown) {
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return error ? (error as ProblemaApi) : undefined;
  }

  return {
    /**
     * Los montos leídos ahora, sin caché, y guardados en ella: «Recargar» tras un
     * cambio simultáneo (#171) arranca la ventana con lo que dejó la otra persona.
     */
    leerMontos: () => cache.fetchQuery({ queryKey: claves.montos(taller, orden), queryFn: () => pedirMontos(orden), staleTime: 0 }),

    cambiarPrecio: async (clave: string, linea: string, precio: number | null) => {
      const { error } = await api.PUT('/api/service-orders/{id}/lines/{lineId}/unit-price', {
        headers: { 'Idempotency-Key': clave },
        params: { path: { id: orden, lineId: linea } },
        body: { unitPrice: precio },
      });
      return terminar(error);
    },

    /** El motivo solo hace falta con la orden finalizada. */
    fijarTotal: async (clave: string, versionActual: number, total: number | null, motivo: string) => {
      const { error } = await api.PUT('/api/service-orders/{id}/total', {
        headers: { 'Idempotency-Key': clave },
        params: { path: { id: orden } },
        body: { version: versionActual, totalAmount: total, ...(motivo.trim() && { reason: motivo.trim() }) },
      });
      return terminar(error);
    },
  };
}
